
import { format } from "date-fns";
import { cmToInches, gToLb } from "@/utils/familyProfile";

// Safe date formatting function that checks if value is "Birth" string
export const formatDateValue = (value: string): string => {
  if (value === "Birth") return value;
  
  try {
    const dateObj = new Date(value);
    if (!isNaN(dateObj.getTime())) {
      return format(dateObj, "MMM d");
    }
    return value;
  } catch (e) {
    return value;
  }
};

// Prepare average data points matched to the main dataset's x-axis values, limited to baby's next 12 months
export const prepareAverageData = (
  dataPoints: any[],
  showAverageLine: boolean,
  getAverageValueForAge: (ageMonths: number) => number,
  valueKey: string,
  babyAgeInMonths: number
): any[] => {
  if (!showAverageLine || dataPoints.length === 0) {
    console.log("[GrowthDataUtils] No average data generated - showAverageLine:", showAverageLine);
    return [];
  }
  
  // Limit the range to the baby's current age plus 12 months
  const maxMonths = babyAgeInMonths + 12;
  
  // Create a range of months from 0 to baby's age + 12 months
  const monthsRange = Array.from(
    { length: maxMonths + 1 }, 
    (_, i) => i
  ).filter(month => month <= maxMonths);
  
  // Generate average values for all months in the range
  const averageValues = monthsRange.map(month => ({
    month,
    averageValue: getAverageValueForAge(month),
    formattedDate: `${month} months`
  }));
  
  console.log(`[GrowthDataUtils] Generated average values for months 0-${maxMonths}:`, averageValues.length);
  return averageValues;
};

// Calculate Y-axis domain that includes all data points (baby data and average)
export const calculateYAxisDomain = (
  dataPoints: any[],
  averageData: any[],
  showAverageLine: boolean,
  valueKey: string
): [number, number] => {
  // First, collect all valid values
  let allValues: number[] = [];
  
  // Add data points from baby's measurements
  dataPoints.forEach(item => {
    if (item && item[valueKey] !== undefined && item[valueKey] !== null) {
      allValues.push(Number(item[valueKey]));
    }
  });
  
  // Add average data points if showing average line
  if (showAverageLine && averageData.length > 0) {
    averageData.forEach(item => {
      if (item && item.averageValue !== undefined && item.averageValue !== null) {
        allValues.push(Number(item.averageValue));
      }
    });
  }
  
  // If no valid values, return default domain
  if (allValues.length === 0) {
    console.log("[GrowthDataUtils] No valid values found for y-axis domain, using default domain");
    return [0, 100]; // Default domain as fallback
  }
  
  // Calculate min and max with some padding
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  
  // Add 10% padding to the domain
  const padding = (maxValue - minValue) * 0.1;
  const min = Math.max(0, minValue - padding); // Don't go below 0
  const max = maxValue + padding;
  
  console.log(`[GrowthDataUtils] Calculated y-axis domain: [${min}, ${max}] from ${allValues.length} values`);
  return [min, max];
};
